import type { FunctionComponent } from 'react'

import { useMediaQuery } from '@material-ui/core'
import { useTheme } from '@material-ui/core/styles'
import {
	BoxInput,
	CurrencyDisplay,
	InfoTooltip,
	useIntlConfig,
	useModal,
} from '@monthio/react-components'
import { useCallback, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

import { ModuleContext } from '../../../../providers/moduleProvider'
import { useBudgetModule } from '../../useBudgetModule'
import { BudgetCategorizedTransactionsTable } from './BudgetCategorizedTransactionsTable'
import { budgetEstimatesCalculatorService } from './budgetEstimatesCalculatorService'
import { applySignBySection } from './budgetEstimatesUtils'
import {
	DataPointName,
	DataPointSectionDiv,
	InitialAmountValue,
} from './styledBudgetEstimates'

const InputWrapper = styled.div`
	display: flex;
	flex-direction: row;
	margin-left: 10px;
`
const TableWrapper = styled.div`
	padding: 24px 16px 16px;
`

interface CategoryPointInputProps {
	value: number
	itemKey: string
	branch: string
	dotKey: string
	children?: never
}

export const CategoryPointInput: FunctionComponent<CategoryPointInputProps> = ({
	itemKey,
	value,
	dotKey,
	branch,
}) => {
	const { t } = useTranslation()
	const { openModal } = useModal()
	const [currentValue, setCurrentValue] = useState(value)
	const initialValue = useRef(value)
	const intlConfig = useIntlConfig()
	const isLarge = useMediaQuery(useTheme().breakpoints.up('sm'))
	const budgetModuleContext = useBudgetModule()

	itemKey = itemKey.replace('tree.', '')

	const handleClick = useCallback(
		(event) => {
			const newValue = !isNaN(event.target.value)
				? event.target.value
				: currentValue
			const valueWithSign = applySignBySection(branch, newValue)

			setCurrentValue(Math.abs(newValue))
			budgetEstimatesCalculatorService.update(dotKey, valueWithSign)
		},
		[branch, currentValue, dotKey],
	)

	const transactionTable = useMemo(
		() => (
			<ModuleContext.Provider value={budgetModuleContext}>
				<BudgetCategorizedTransactionsTable
					itemKey={itemKey}
					omitTransactionsIfAny={initialValue.current == 0}
					estimate={value}
				/>
			</ModuleContext.Provider>
		),
		[itemKey, value, budgetModuleContext],
	)

	const showTransactions = useCallback(async () => {
		const bodyRender = () => {
			return <TableWrapper>{transactionTable}</TableWrapper>
		}

		bodyRender.displayName = 'TransactionsModal'
		await openModal({
			title: t(`${itemKey}-header`),
			maxWidth: 'sm',
			bodyRender,
		})
	}, [itemKey, openModal, t, transactionTable])

	const hasChange = Math.abs(initialValue.current) !== Math.abs(currentValue)

	return (
		<DataPointSectionDiv onClick={showTransactions}>
			<InfoTooltip
				tooltip={isLarge ? t(`${itemKey}-tooltip`) : transactionTable}
				modalTitle={t(`${itemKey}-header`)}
				variant="search"
			>
				<DataPointName>{t(`${itemKey}-header`)}</DataPointName>
			</InfoTooltip>
			<InputWrapper onClick={(event) => event.stopPropagation()}>
				{hasChange ? (
					<InitialAmountValue>
						(
						<CurrencyDisplay
							amount={Math.abs(initialValue.current)}
							symbolMode={false}
						/>
						)
					</InitialAmountValue>
				) : null}
				<BoxInput
					value={Math.abs(currentValue)}
					onChange={handleClick}
					currencyInputProps={{
						allowDecimals: false,
						allowNegativeValue: false,
						intlConfig,
						maxLength: 8,
					}}
					type="tel"
				/>
			</InputWrapper>
		</DataPointSectionDiv>
	)
}
